import React, { useEffect, useState, useMemo } from "react";
import { obtenerMayoresDiferencias } from "./api/catalogo";
import "./MayoresDiferencias.css";
import { Link } from "react-router-dom";
import {
    Search, RefreshCw, TrendingUp, TrendingDown,
    ArrowUpDown, ArrowUp, ArrowDown,
    Sparkles, PackageX,
} from "lucide-react";

/* ── Helpers ───────────────────────────────────────────────── */
const fmt = (n) =>
    Number(n).toLocaleString("es-CL", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtDiff = (n) => (n > 0 ? "+" : "") + fmt(n);

const fmtDate = (ms) => {
    if (!ms) return "—";
    return new Date(ms).toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" });
};

const fmtDateTs = (ts) => {
    if (!ts) return "—";
    return new Date(ts * 1000).toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" });
};

/* ── SortIcon ──────────────────────────────────────────────── */
function SortIcon({ field, sortField, sortDir }) {
    if (sortField !== field) return <ArrowUpDown size={11} style={{ marginLeft: 3, opacity: 0.35 }} />;
    return sortDir === "asc"
        ? <ArrowUp size={11} style={{ marginLeft: 3, color: "#2563eb" }} />
        : <ArrowDown size={11} style={{ marginLeft: 3, color: "#2563eb" }} />;
}

/* ── TAB IDs ───────────────────────────────────────────────── */
const TAB_PRECIOS = "precios";
const TAB_NUEVOS = "nuevos";
const TAB_DESAPARECIDOS = "desaparecidos";

/* ── Component ─────────────────────────────────────────────── */
function MayoresDiferencias() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState(TAB_PRECIOS);

    /* — estado pestaña precios — */
    const [sortField, setSortField] = useState("diferenciaAbs");
    const [sortDir, setSortDir] = useState("desc");
    const [filtroDir, setFiltroDir] = useState("todos");

    /* — estado pestaña nuevos — */
    const [sortNuevos, setSortNuevos] = useState("marca");
    const [sortDirNuevos, setSortDirNuevos] = useState("asc");

    /* — estado pestaña desaparecidos — */
    const [sortDesp, setSortDesp] = useState("marca");
    const [sortDirDesp, setSortDirDesp] = useState("asc");

    const setSort = (field) => {
        if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortField(field); setSortDir("desc"); }
    };
    const setSortN = (field) => {
        if (sortNuevos === field) setSortDirNuevos((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortNuevos(field); setSortDirNuevos("asc"); }
    };
    const setSortD = (field) => {
        if (sortDesp === field) setSortDirDesp((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortDesp(field); setSortDirDesp("asc"); }
    };

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const d = await obtenerMayoresDiferencias();
            if (!d) {
                setData(null);
                setError("No hay datos registrados aún.");
            } else {
                setData(d);
                if (d.generadoEn) {
                    localStorage.setItem("lastSeenDiferenciasTs", String(d.generadoEn));
                }
            }
        } catch (err) {
            setError(err.message || "Error al cargar datos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    /* ── Filas ordenadas pestaña PRECIOS ─────────────────────── */
    const sortedRows = useMemo(() => {
        if (!data?.top) return [];
        let rows = [...data.top];
        if (filtroDir === "subidas") rows = rows.filter((r) => r.diferencia > 0);
        else if (filtroDir === "bajadas") rows = rows.filter((r) => r.diferencia < 0);
        const dir = sortDir === "asc" ? 1 : -1;
        rows.sort((a, b) => {
            let va = a[sortField], vb = b[sortField];
            if (typeof va === "string") va = va.toLowerCase();
            if (typeof vb === "string") vb = vb.toLowerCase();
            return va > vb ? dir : va < vb ? -dir : 0;
        });
        return rows;
    }, [data, sortField, sortDir, filtroDir]);

    /* ── Filas ordenadas pestaña NUEVOS ─────────────────────── */
    const sortedNuevos = useMemo(() => {
        if (!data?.nuevos) return [];
        const rows = [...data.nuevos];
        const dir = sortDirNuevos === "asc" ? 1 : -1;
        rows.sort((a, b) => {
            const va = String(a[sortNuevos] ?? "").toLowerCase();
            const vb = String(b[sortNuevos] ?? "").toLowerCase();
            return va > vb ? dir : va < vb ? -dir : 0;
        });
        return rows;
    }, [data, sortNuevos, sortDirNuevos]);

    /* ── Filas ordenadas pestaña DESAPARECIDOS ───────────────── */
    const sortedDesp = useMemo(() => {
        if (!data?.desaparecidos) return [];
        const rows = [...data.desaparecidos];
        const dir = sortDirDesp === "asc" ? 1 : -1;
        rows.sort((a, b) => {
            const va = String(a[sortDesp] ?? "").toLowerCase();
            const vb = String(b[sortDesp] ?? "").toLowerCase();
            return va > vb ? dir : va < vb ? -dir : 0;
        });
        return rows;
    }, [data, sortDesp, sortDirDesp]);

    /* ── Stats chips filtro precios ──────────────────────────── */
    const stats = useMemo(() => {
        if (!data?.top) return { subidas: 0, bajadas: 0 };
        return {
            subidas: data.top.filter((r) => r.diferencia > 0).length,
            bajadas: data.top.filter((r) => r.diferencia < 0).length,
        };
    }, [data]);

    /* ── Helper precio legible para nuevos/desaparecidos ──────── */
    const precioResumen = (item) => {
        if (!Array.isArray(item.precios) || item.precios.length === 0) return "—";
        return item.precios
            .slice(0, 2)
            .map((p) => `$${Number(p.valor).toLocaleString("es-CL")}`)
            .join(" / ");
    };

    /* ── Fechas legibles ─────────────────────────────────────── */
    const fechaActualStr = data?.fechaActual ? fmtDateTs(data.fechaActual) : null;
    const fechaAnteriorStr = data?.fechaAnterior ? fmtDateTs(data.fechaAnterior) : null;

    /* ── Tab config ──────────────────────────────────────────── */
    const tabs = [
        {
            id: TAB_PRECIOS,
            label: "Novedades de precio",
            icon: <TrendingUp size={14} />,
            count: data?.top?.length ?? 0,
            color: "#2563eb",
            bg: "#eff6ff",
        },
        {
            id: TAB_NUEVOS,
            label: "Modelos nuevos",
            icon: <Sparkles size={14} />,
            count: data?.nuevos?.length ?? 0,
            color: "#16a34a",
            bg: "#f0fdf4",
        },
        {
            id: TAB_DESAPARECIDOS,
            label: "Ya no disponibles",
            icon: <PackageX size={14} />,
            count: data?.desaparecidos?.length ?? 0,
            color: "#dc2626",
            bg: "#fef2f2",
        },
    ];

    /* ── Render ──────────────────────────────────────────────── */
    return (
        <>
            <div className="diferencias-main">

                {/* Header */}
                <div className="diferencias-header">
                    <div className="diferencias-header-left">
                        <img src="/logo_doctorcar.png" alt="Logo" className="diferencias-header-logo" />
                        <div>
                            <h2 className="diferencias-header-title">Novedades del mercado</h2>
                            {data?.generadoEn && (
                                <p className="diferencias-header-meta">
                                    Actualizado {fmtDate(data.generadoEn)}
                                    {fechaActualStr && fechaAnteriorStr && (
                                        <span style={{ marginLeft: 8, color: "#94a3b8" }}>
                                            · comparando {fechaAnteriorStr} → {fechaActualStr}
                                        </span>
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                    <button className="df-btn" onClick={loadData} disabled={loading}>
                        <RefreshCw size={14} style={loading ? { animation: "spin 1s linear infinite" } : {}} />
                        Actualizar
                    </button>
                </div>

                {/* Loading */}
                {loading && (
                    <div className="diferencias-state">
                        <div className="diferencias-state-icon">⏳</div>
                        <p>Cargando novedades…</p>
                    </div>
                )}

                {/* Error */}
                {!loading && error && (
                    <div className="diferencias-error">
                        <span>{error}</span>
                        <button className="df-btn" onClick={loadData}>Reintentar</button>
                    </div>
                )}

                {/* Contenido */}
                {!loading && !error && data && (
                    <>
                        {/* ── Pestañas ───────────────────────────────────── */}
                        <div style={{
                            display: "flex", gap: 6, borderBottom: "1.5px solid #e2e8f0",
                            marginBottom: 16, paddingBottom: 0,
                        }}>
                            {tabs.map((t) => {
                                const active = tab === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => setTab(t.id)}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 6,
                                            padding: "8px 14px",
                                            border: "none", borderBottom: active ? `2.5px solid ${t.color}` : "2.5px solid transparent",
                                            background: "transparent",
                                            color: active ? t.color : "#64748b",
                                            fontWeight: active ? 700 : 500,
                                            fontSize: 13,
                                            cursor: "pointer",
                                            fontFamily: "inherit",
                                            borderRadius: "4px 4px 0 0",
                                            transition: "color .15s, border-color .15s",
                                            marginBottom: -1.5,
                                        }}
                                    >
                                        <span style={{
                                            display: "flex", alignItems: "center",
                                            color: active ? t.color : "#94a3b8",
                                        }}>{t.icon}</span>
                                        {t.label}
                                        <span style={{
                                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                                            minWidth: 20, height: 20, borderRadius: 999,
                                            background: active ? t.bg : "#f1f5f9",
                                            color: active ? t.color : "#94a3b8",
                                            fontSize: 11, fontWeight: 700, padding: "0 5px",
                                        }}>
                                            {t.count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* ══════════════════════════════════════════════════
                PESTAÑA 1 — NOVEDADES DE PRECIO
            ══════════════════════════════════════════════════ */}
                        {tab === TAB_PRECIOS && (
                            <>
                                <div className="diferencias-summary">
                                    <span className="diferencias-summary-text">
                                        <strong>{data.totalModelos}</strong> modelos analizados ·{" "}
                                        mostrando <strong>{sortedRows.length}</strong> con mayor variación
                                    </span>
                                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                        {[
                                            { id: "todos", label: `Todos (${data.top.length})`, bg: filtroDir === "todos" ? "#1e293b" : "transparent", color: filtroDir === "todos" ? "#fff" : "#64748b", border: filtroDir === "todos" ? "#1e293b" : "#e2e8f0", icon: null },
                                            { id: "subidas", label: `Subidas (${stats.subidas})`, bg: filtroDir === "subidas" ? "#fef2f2" : "transparent", color: filtroDir === "subidas" ? "#dc2626" : "#64748b", border: filtroDir === "subidas" ? "#fca5a5" : "#e2e8f0", icon: <TrendingUp size={13} /> },
                                            { id: "bajadas", label: `Bajadas (${stats.bajadas})`, bg: filtroDir === "bajadas" ? "#f0fdf4" : "transparent", color: filtroDir === "bajadas" ? "#16a34a" : "#64748b", border: filtroDir === "bajadas" ? "#86efac" : "#e2e8f0", icon: <TrendingDown size={13} /> },
                                        ].map((f) => (
                                            <button key={f.id} onClick={() => setFiltroDir(f.id)} style={{
                                                display: "flex", alignItems: "center", gap: 5,
                                                padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                                                cursor: "pointer", border: `1.5px solid ${f.border}`,
                                                fontFamily: "inherit", background: f.bg, color: f.color,
                                            }}>
                                                {f.icon}{f.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="diferencias-table-wrap">
                                    <table className="diferencias-table">
                                        <thead>
                                            <tr>
                                                {[
                                                    { key: "marca", label: "Marca" },
                                                    { key: "model", label: "Modelo" },
                                                    { key: "modelDetail", label: "Versión" },
                                                    { key: "tipoPrecio", label: "Tipo precio" },
                                                ].map((col) => (
                                                    <th key={col.key}
                                                        className={`sortable ${sortField === col.key ? "sorted" : ""}`}
                                                        onClick={() => setSort(col.key)}>
                                                        {col.label}
                                                        <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                                                    </th>
                                                ))}
                                                {[
                                                    { key: "precioAnterior", label: "Precio anterior" },
                                                    { key: "precioActual", label: "Precio actual" },
                                                    { key: "diferenciaAbs", label: "Variación" },
                                                ].map((col) => (
                                                    <th key={col.key}
                                                        className={`sortable num ${sortField === col.key ? "sorted" : ""}`}
                                                        onClick={() => setSort(col.key)}>
                                                        {col.label}
                                                        <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                                                    </th>
                                                ))}
                                                <th style={{ width: 56, textAlign: "center" }}>
                                                    <Search size={13} style={{ opacity: 0.4 }} />
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedRows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={8} style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 14 }}>
                                                        Sin {filtroDir === "subidas" ? "subidas" : filtroDir === "bajadas" ? "bajadas" : "variaciones"} en este período
                                                    </td>
                                                </tr>
                                            ) : sortedRows.map((item, i) => {
                                                const isUp = item.diferencia > 0;
                                                const pct = item.precioAnterior
                                                    ? ((item.diferencia / item.precioAnterior) * 100).toFixed(1)
                                                    : null;
                                                return (
                                                    <tr key={`${item.idActual}-${i}`}>
                                                        <td><span className="df-marca">{item.marca}</span></td>
                                                        <td style={{ fontWeight: 500, color: "#111827" }}>{item.model}</td>
                                                        <td style={{ color: "#6b7280" }}>{item.modelDetail}</td>
                                                        <td><span className="df-tipo">{item.tipoPrecio}</span></td>
                                                        <td className="num">
                                                            <span className="df-precio-anterior">${fmt(item.precioAnterior)}</span>
                                                        </td>
                                                        <td className="num">
                                                            <span className="df-precio">${fmt(item.precioActual)}</span>
                                                        </td>
                                                        <td className="num">
                                                            <span className={`df-diff ${isUp ? "df-diff--up" : "df-diff--down"}`}>
                                                                <span className="df-diff-arrow">{isUp ? "▲" : "▼"}</span>
                                                                {fmtDiff(item.diferencia)}
                                                                {pct && (
                                                                    <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.75 }}>
                                                                        ({pct}%)
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <Link to={`/modelo/${item.idActual}`} className="df-action-btn" title="Ver detalle">
                                                                <Search size={14} strokeWidth={2} />
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {/* ══════════════════════════════════════════════════
                PESTAÑA 2 — MODELOS NUEVOS
            ══════════════════════════════════════════════════ */}
                        {tab === TAB_NUEVOS && (
                            <>
                                <div className="diferencias-summary">
                                    <span className="diferencias-summary-text">
                                        <strong>{sortedNuevos.length}</strong> modelos que aparecen por primera vez
                                        {fechaActualStr && (
                                            <span style={{ color: "#94a3b8", marginLeft: 6 }}>en la carga del {fechaActualStr}</span>
                                        )}
                                    </span>
                                </div>

                                <div className="diferencias-table-wrap">
                                    <table className="diferencias-table">
                                        <thead>
                                            <tr>
                                                {[
                                                    { key: "marca", label: "Marca" },
                                                    { key: "model", label: "Modelo" },
                                                    { key: "modelDetail", label: "Versión" },
                                                ].map((col) => (
                                                    <th key={col.key}
                                                        className={`sortable ${sortNuevos === col.key ? "sorted" : ""}`}
                                                        onClick={() => setSortN(col.key)}>
                                                        {col.label}
                                                        <SortIcon field={col.key} sortField={sortNuevos} sortDir={sortDirNuevos} />
                                                    </th>
                                                ))}
                                                <th>Precio(s)</th>
                                                <th style={{ width: 56, textAlign: "center" }}>
                                                    <Search size={13} style={{ opacity: 0.4 }} />
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedNuevos.length === 0 ? (
                                                <tr>
                                                    <td colSpan={5} style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 14 }}>
                                                        No se detectaron modelos nuevos en este período
                                                    </td>
                                                </tr>
                                            ) : sortedNuevos.map((item, i) => (
                                                <tr key={`nuevo-${item.id}-${i}`}>
                                                    <td>
                                                        <span className="df-marca">{item.marca}</span>
                                                    </td>
                                                    <td style={{ fontWeight: 500, color: "#111827" }}>{item.model}</td>
                                                    <td style={{ color: "#6b7280" }}>{item.modelDetail || "—"}</td>
                                                    <td>
                                                        {Array.isArray(item.precios) && item.precios.length > 0 ? (
                                                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                                                {item.precios.slice(0, 3).map((p, pi) => (
                                                                    <span key={pi} style={{
                                                                        display: "inline-flex", flexDirection: "column",
                                                                        fontSize: 11, lineHeight: 1.3,
                                                                    }}>
                                                                        <span style={{ color: "#94a3b8", fontSize: 10 }}>{p.tipo}</span>
                                                                        <span style={{ fontWeight: 600, color: "#111827" }}>
                                                                            ${Number(p.valor).toLocaleString("es-CL")}
                                                                        </span>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: "#d1d5db" }}>—</span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: "center" }}>
                                                        <Link to={`/modelo/${item.id}`} className="df-action-btn" title="Ver detalle">
                                                            <Search size={14} strokeWidth={2} />
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {/* ══════════════════════════════════════════════════
                PESTAÑA 3 — MODELOS DESAPARECIDOS
            ══════════════════════════════════════════════════ */}
                        {tab === TAB_DESAPARECIDOS && (
                            <>
                                <div className="diferencias-summary">
                                    <span className="diferencias-summary-text">
                                        <strong>{sortedDesp.length}</strong> modelos que estaban en la carga anterior y ya no aparecen
                                        {fechaAnteriorStr && fechaActualStr && (
                                            <span style={{ color: "#94a3b8", marginLeft: 6 }}>
                                                ({fechaAnteriorStr} → {fechaActualStr})
                                            </span>
                                        )}
                                    </span>
                                </div>

                                <div className="diferencias-table-wrap">
                                    <table className="diferencias-table">
                                        <thead>
                                            <tr>
                                                {[
                                                    { key: "marca", label: "Marca" },
                                                    { key: "model", label: "Modelo" },
                                                    { key: "modelDetail", label: "Versión" },
                                                ].map((col) => (
                                                    <th key={col.key}
                                                        className={`sortable ${sortDesp === col.key ? "sorted" : ""}`}
                                                        onClick={() => setSortD(col.key)}>
                                                        {col.label}
                                                        <SortIcon field={col.key} sortField={sortDesp} sortDir={sortDirDesp} />
                                                    </th>
                                                ))}
                                                <th>Último precio conocido</th>
                                                <th style={{ width: 56, textAlign: "center" }}>
                                                    <Search size={13} style={{ opacity: 0.4 }} />
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedDesp.length === 0 ? (
                                                <tr>
                                                    <td colSpan={5} style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 14 }}>
                                                        No se detectaron modelos desaparecidos en este período
                                                    </td>
                                                </tr>
                                            ) : sortedDesp.map((item, i) => (
                                                <tr key={`desp-${item.id}-${i}`} style={{ opacity: 0.82 }}>
                                                    <td>
                                                        <span className="df-marca" style={{ opacity: 0.7 }}>{item.marca}</span>
                                                    </td>
                                                    <td style={{ fontWeight: 500, color: "#374151" }}>{item.model}</td>
                                                    <td style={{ color: "#9ca3af" }}>{item.modelDetail || "—"}</td>
                                                    <td>
                                                        {Array.isArray(item.precios) && item.precios.length > 0 ? (
                                                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                                                {item.precios.slice(0, 3).map((p, pi) => (
                                                                    <span key={pi} style={{
                                                                        display: "inline-flex", flexDirection: "column",
                                                                        fontSize: 11, lineHeight: 1.3,
                                                                    }}>
                                                                        <span style={{ color: "#94a3b8", fontSize: 10 }}>{p.tipo}</span>
                                                                        <span style={{ fontWeight: 600, color: "#6b7280", textDecoration: "line-through" }}>
                                                                            ${Number(p.valor).toLocaleString("es-CL")}
                                                                        </span>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: "#d1d5db" }}>—</span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: "center" }}>
                                                        <Link to={`/modelo/${item.id}`} className="df-action-btn" title="Ver historial"
                                                            style={{ opacity: 0.6 }}>
                                                            <Search size={14} strokeWidth={2} />
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
        </>
    );
}

export default MayoresDiferencias;